import { jwtDecode } from 'jwt-decode';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContextProps, UserProps } from '../types/userContextTypes';
import { decryptData, encryptData } from '../helpers/encryptionUtils';
import { RoleName } from '../enums/RoleNameEnm';

export const UserContext = createContext<UserContextProps>({
  setUserInfo: () => {},
  removeUserInfo: () => {},
  IsAuthenticated: () => false,
  isTokenExpired: false,
  setIsTokenExpired: () => {},
  setUserInfoField: () => {},
  getUserInfo: () => '',
  getPermissions: () => [],
  setUserToken: () => {},
  getUserToken: () => '',
  setProfileImageToContext: () => {},
  setIsPublicPage: () => {},
  getIsPublicPage: () => true,
  setCurrentPage: () => {},
  getCurrentPage: () => '',
});

export const UserContextProvider = ({ children }: React.PropsWithChildren) => {
  const [isTokenExpired, setIsTokenExpired] = useState<boolean>(false);
  const [token, setToken] = useState<string>(localStorage.getItem('token') || '');

  const initialUserProps = (): UserProps => ({
    userId: localStorage.getItem('userId') ? (localStorage.getItem('userId') as string) : '',
    email: localStorage.getItem('userEmail') ? (localStorage.getItem('userEmail') as string) : '',
    deviceToken: localStorage.getItem('deviceToken')
      ? (localStorage.getItem('deviceToken') as string)
      : '',
    passwordExpiredAt: localStorage.getItem('passwordExpiredAt')
      ? Number(localStorage.getItem('passwordExpiredAt'))
      : 0,
    roleName: (localStorage.getItem('roleName') as RoleName | null) ?? undefined,
    permissions:
      localStorage.getItem('permissions') !== 'undefined'
        ? JSON.parse(decryptData(localStorage.getItem('permissions') || '[]') || '[]')
        : ['userAccessManagement_all'],
    name: localStorage.getItem('name') ? (localStorage.getItem('name') as string) : '',
  });

  const [userInfoState, setUserInfoState] = useState<UserProps>(initialUserProps);
  const [profileImageState, setProfileImageState] = useState<string>('');
  const [isPublic, setIsPublic] = useState<boolean>(true);
  const [currentPage, setCurrentPageState] = useState<string>('');
  const navigate = useNavigate();

  const setUserInfo = (value: UserProps) => {
    let { email, passwordExpiredAt, permissions, deviceToken, roleName, name, userId } = value;
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('passwordExpiredAt', passwordExpiredAt?.toString());
    if (roleName) {
      localStorage.setItem('roleName', roleName);
    }
    if (deviceToken != undefined) {
      localStorage.setItem('deviceToken', deviceToken);
    }
    localStorage.setItem('permissions', encryptData(JSON.stringify(permissions)));
    localStorage.setItem('name', name);

    setUserInfoState((prev) => ({
      ...prev,
      email,
      permissions,
      roleName,
      deviceToken,
      passwordExpiredAt,
      name,
    }));
  };

  const setCurrentPage = (value: string) => {
    setCurrentPageState(value);
  };

  const getCurrentPage = () => {
    return currentPage;
  };

  const setIsPublicPage = (value: boolean) => {
    setIsPublic(value);
  };

  const getIsPublicPage = () => {
    return isPublic;
  };
  const setUserInfoField = <T extends string | number | boolean>(
    userInfoField: string,
    value: T
  ) => {
    setUserInfoState((prev: any) => ({
      ...prev,
      [userInfoField]: value,
    }));
    localStorage.setItem(userInfoField, String(value));
  };

  const setProfileImageToContext = (value: string) => {
    setProfileImageState(value);
  };

  const IsAuthenticated = useCallback((): boolean => {
    let tokenVal: string | null = token;
    try {
      if (tokenVal) {
        const { exp } = jwtDecode(tokenVal) as any;
        const isValid = Date.now() < exp * 1000;

        if (!isValid && !navigator.onLine) {
          return true;
        }

        return isValid;
      }
      return false;
    } catch (err) {
      return false;
    }
  }, [token]);

  const removeUserInfo = () => {
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('companyId');
    localStorage.removeItem('theme');
    localStorage.removeItem('permissions');
    localStorage.removeItem('passwordExpiredAt');
    localStorage.removeItem('roleName');
    localStorage.removeItem('token');
    localStorage.removeItem('name');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    setUserInfoState(initialUserProps);
  };

  const getUserInfo = (field: string) => {
    const value = localStorage.getItem(field);
    if (value) {
      return value;
    }

    return null;
  };

  const setUserToken = (token: string) => {
    if (token) {
      localStorage.setItem('token', token);
      setToken(token);
    }
  };

  const getUserToken = () => {
    if (token) return token;

    return localStorage.getItem('token') || '';
  };

  const getPermissions = useCallback(() => {
    return userInfoState.permissions;
  }, [userInfoState]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!navigator.onLine) {
        return;
      }

      const expiryTime = Number(localStorage.getItem('passwordExpiredAt'));
      if (expiryTime && new Date() > new Date(expiryTime)) {
        localStorage.clear();
        navigate('/signIn');
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <UserContext.Provider
      value={{
        userInfoState,
        setUserInfo,
        removeUserInfo,
        IsAuthenticated,
        isTokenExpired,
        setIsTokenExpired,
        setUserInfoField,
        getUserInfo,
        getPermissions,
        setUserToken,
        getUserToken,
        profileImageState,
        setProfileImageToContext,
        setIsPublicPage,
        getIsPublicPage,
        setCurrentPage,
        getCurrentPage,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;

export const useUserContext = (): UserContextProps => {
  return useContext(UserContext);
};
